import { parseIntOrDefault } from '@jky/utilities';
import { QuantitySelectorUpdateEvent } from '@jky/events';
import { Component } from '@jky/component';

if (!customElements.get('jky-quantity-selector')) {
    class JkyQuantitySelectorComponent extends Component {
        requiredRefs = ['quantityInput', 'minusButton', 'plusButton'];
        serverDisabledMinus = false;
        serverDisabledPlus = false;
        initialized = false;
        connectedCallback() {
            super.connectedCallback();
            if (this.refs.minusButton.disabled) {
                this.serverDisabledMinus = true;
            }

            if (this.refs.plusButton.disabled) {
                this.serverDisabledPlus = true;
            }

            this.initialized = true;
            this.updateButtonStates();
        }
        disconnectedCallback() {
            super.disconnectedCallback();
        }

        /**
         * 更新购物车数量并刷新组件状态
         * @param {number} cartQty - 购物车中该变体的数量
         */
        setCartQuantity(cartQty) {
            this.refs.quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
            this.updateCartQuantity();
        }

        /**
         * 获取当前数量选择器的值
         * @returns {string} The current value
         */
        getValue() {
            return this.refs.quantityInput.value;
        }

        /**
         * 设置当前数量选择器的值
         * @param {string} value - 要设置的值
         */
        setValue(value) {
            this.refs.quantityInput.value = value;
        }

        /**
         * 从DOM中获取当前值[最小值、最大值、步长、当前值、购物车中该变体总数量]
         * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number}}
         */
        getCurrentValues() {
            const { quantityInput } = this.refs;
            return {
                min: parseIntOrDefault(quantityInput.min, 1),
                max: parseIntOrDefault(quantityInput.max, null),
                step: parseIntOrDefault(quantityInput.step, 1),
                value: parseIntOrDefault(quantityInput.value, 0),
                cartQuantity: parseIntOrDefault(quantityInput.getAttribute('data-cart-quantity'), 0),
            };
        }

        /**
         * 获取此数量选择器的有效最大值
         * 产品页面：max - cartQuantity
         * 购物车页面：max
         * @returns {number | null} 有效最大值，若无最大值则为 null
         */
        getEffectiveMax() {
            const { max, cartQuantity, min } = this.getCurrentValues();
            if (max === null) return null;
            if(this.inCart()) return max;
            return Math.max(max - cartQuantity, min);
        }

        /**
         * 根据当前值和限制更新按钮状态
         */
        updateButtonStates() {
            const { minusButton, plusButton } = this.refs;
            const { min, value } = this.getCurrentValues();
            const effectiveMax = this.getEffectiveMax();

            // Only manage buttons that weren't server-disabled
            minusButton.disabled = value <= min;

            if(effectiveMax !== null && value >= effectiveMax) {
              plusButton.setAttribute('data-disabled', '');
            } else {
              plusButton.removeAttribute('data-disabled');
            }
            // plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
        }

        /**
         * 更新当前数量选择器的值
         * @param {number} stepMultiplier - 正数增加，负数减少步长
         */
        updateQuantity(stepMultiplier) {
            const { quantityInput } = this.refs;
            const { min, step, value } = this.getCurrentValues();
            const effectiveMax = this.getEffectiveMax();
            const newValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value + step * stepMultiplier));
            quantityInput.value = newValue.toString();
            this.onQuantityChange();
            this.updateButtonStates();
        }

        /**
         * plus按钮点击事件处理
         * @param {Event} event - The event.
         */
        increaseQuantity(event) {
            if (!(event.target instanceof HTMLElement)) return;
            if(event.target.hasAttribute('data-disabled')) {
                try {
                    const { quantityInput } = this.refs;
                    const tips = this.closest('jky-cart')?.dataset.quantityMaxTips ?? 'The quantity of the product in the $index row of the shopping cart has reached the maximum limit.';
                    pushErrorMessage(tips.replace('$index', Number(quantityInput.dataset.cartLine)));
                } catch (_) {

                }
                return;
            }
            event.preventDefault();
            this.updateQuantity(1);
        }

        /**
         * minus按钮点击事件处理
         * @param {Event} event - The event.
         */
        decreaseQuantity(event) {
            if (!(event.target instanceof HTMLElement)) return;
            event.preventDefault();
            this.updateQuantity(-1);
        }

        /**
         * 当输入框获得焦点时，全选输入框内容【方便用户直接输入数量覆盖当前值】
         * @param {FocusEvent} event
         */
        selectInputValue(event) {
            const { quantityInput } = this.refs;
            if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;
            quantityInput.select();
        }

        /**
         * 处理数量设置事件（失去焦点时）。
         * 验证并调整到有效值。
         * @param {Event} event - The event.
         */
        setQuantity(event) {
            if (!(event.target instanceof HTMLInputElement)) return;
            event.preventDefault();

            const { quantityInput } = this.refs;
            const { min, step } = this.getCurrentValues();
            const effectiveMax = this.getEffectiveMax();

            // 边界处理
            const quantity = Math.min(effectiveMax ?? Infinity, Math.max(min, parseInt(event.target.value) || 0));

            // 校验步长是否符合要求
            if ((quantity - min) % step !== 0) {
                // 设置无效值并触发原生 HTML 校验
                quantityInput.value = quantity.toString();
                quantityInput.reportValidity();
                return;
            }

            quantityInput.value = quantity.toString();
            this.onQuantityChange();
            this.updateButtonStates();
        }

        /**
         * 处理数量选择器值的变更
         */
        onQuantityChange() {
            const { quantityInput } = this.refs;
            const newValue = parseInt(quantityInput.value);
            this.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine) || undefined));
        }

        /**
         * 更新购物车数量并刷新按钮状态
         */
        updateCartQuantity() {
            const { quantityInput } = this.refs;
            const { min, value } = this.getCurrentValues();
            const effectiveMax = this.getEffectiveMax();

            // 边界处理
            const clampedValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value));
            if (clampedValue !== value) {
                quantityInput.value = clampedValue.toString();
            }
            this.updateButtonStates();
        }
        inCart() {
            // 判断是否在jky-cart节点中使用
            const cart = document.querySelector('jky-cart');
            if(!cart) return false;
            return cart?.contains(this) ?? false;
        }
    }

    customElements.define('jky-quantity-selector', JkyQuantitySelectorComponent);
}